import { useEffect, useState, type ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import { Sidebar } from './Sidebar';
import { Header } from './Header';

/**
 * AppShell — overall page chrome for Oscar.
 *
 * Follows the Insights site report template
 * (insights/site/reports/Resources/report-template.html):
 *   - Sticky navy header with 3px orange bottom border (in Header.tsx)
 *   - Sidebar navigation on the left (slides in as a drawer on <md)
 *   - Main content area with f4f6f9 background
 *   - Navy footer with the "Commercial in Confidence · Internal Use Only"
 *     classification line, mandated for all internal Onsite tools.
 *
 * Responsive behaviour:
 *   - <md (Tailwind ≤768px): sidebar collapses to an off-canvas drawer
 *     toggled by a hamburger button in the Header.
 *   - md+: sidebar is static in the flex row.
 *
 * The drawer auto-closes on route change so navigating from a NavLink on
 * mobile does not leave the overlay covering the page.
 */
const COLLAPSE_KEY = 'oscar.sidebarCollapsed';

export function AppShell({ children }: { children: ReactNode }) {
  const year = new Date().getFullYear();
  const [navOpen, setNavOpen] = useState(false);
  // Desktop sidebar collapse, remembered across visits.
  const [collapsed, setCollapsed] = useState(() => {
    try { return localStorage.getItem(COLLAPSE_KEY) === '1'; } catch { return false; }
  });
  const location = useLocation();

  useEffect(() => {
    setNavOpen(false);
  }, [location.pathname]);

  const toggleCollapsed = () => setCollapsed((v) => {
    const next = !v;
    try { localStorage.setItem(COLLAPSE_KEY, next ? '1' : '0'); } catch { /* ignore */ }
    return next;
  });

  return (
    <div className="min-h-screen flex flex-col bg-bg">
      <Header onMenuClick={() => setNavOpen((v) => !v)} navOpen={navOpen} />
      <div className="flex-1 flex min-h-0 relative">
        <Sidebar open={navOpen} onClose={() => setNavOpen(false)} collapsed={collapsed} onToggleCollapse={toggleCollapsed} />
        <main className="flex-1 min-w-0 max-w-full px-3 sm:px-4 lg:px-6 py-4 lg:py-6 overflow-x-hidden overflow-y-auto">
          {children}
        </main>
      </div>
      <footer className="bg-navy text-white/65 text-center px-4 sm:px-6 py-3 text-[11px] sm:text-[12px] tracking-wide flex flex-wrap items-center justify-center gap-x-1.5 gap-y-0.5">
        <span>Commercial in Confidence</span>
        <span className="opacity-35 hidden sm:inline">&nbsp;·&nbsp;</span>
        <span>Internal Use Only</span>
        <span className="opacity-35 hidden sm:inline">&nbsp;·&nbsp;</span>
        <span>Onsite Rental Group &copy; {year}</span>
      </footer>
    </div>
  );
}
